import Navbar from "@/components/Navbar";
import Footer from "@/components/sections/Footer";
import { Button } from "@/components/ui/button";
import SEOHead from "@/components/SEOHead";
import { CheckCircle2, PhoneCall, ShieldCheck } from "lucide-react";
import {
  RelatedPages,
  allSpravatoScenarioLinks,
  allTreatmentsLinks,
  allDecisionLinks,
} from "@/components/RelatedPages";
import { SpravatoSafetyInfo } from "@/components/SpravatoSafetyInfo";
import {
  SpravatoFaqSection,
  SpravatoConsultSection,
  faqsForSchema,
} from "@/components/SpravatoSections";
import Breadcrumbs from "@/components/Breadcrumbs";
import { buildBreadcrumbList, Crumb } from "@/components/schemaConstants";

const crumbs: Crumb[] = [
  { name: "Services", href: "/services-directory" },
  { name: "SPRAVATO®", href: "/services/spravato" },
];

const PHONE = "954-827-9793";
const PHONE_TEL = "+19548279793";
const visitSteps = [
  {
    n: "01",
    title: "Before you arrive.",
    body: "Don't eat for at least 2 hours before your appointment, and don't drink anything for at least 30 minutes before — this reduces the chance of nausea. If you use a nasal steroid or decongestant, take it at least an hour beforehand. Arrange your ride home before you come; you won't be able to drive.",
  },
  {
    n: "02",
    title: "Check-in.",
    body: "We'll check your blood pressure and review how you've been doing since your last visit.",
  },
  {
    n: "03",
    title: "Dosing.",
    body: "You'll spray the medication yourself, following our team's instructions. It takes a few minutes.",
  },
  {
    n: "04",
    title: "Monitoring.",
    body: "You'll stay with us for at least 2 hours while a member of our clinical team monitors you. We'll check your blood pressure again during this period.",
  },
  {
    n: "05",
    title: "The treatment room.",
    body: "You'll relax in a comfortable recliner in a calm, private treatment space. We provide blankets and eye masks, and you're welcome to bring headphones and your own music. The lighting is kept low and soothing so you can rest quietly during the monitoring period.",
  },
  {
    n: "06",
    title: "Going home.",
    body: "When our team determines you're ready, your ride takes you home. You shouldn't drive, operate machinery, or do anything requiring full alertness until the next day, after a restful night's sleep.",
  },
];
const Spravato = () => {
  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={"SPRAVATO® Treatment | Neuro Wellness TMS"}
        description={
          "Neuro Wellness TMS Centers of America is a certified SPRAVATO® REMS treatment center in Coral Springs, FL. If antidepressants haven't worked, request a consultation to learn about SPRAVATO® for treatment-resistant depression."
        }
        canonical={"/services/spravato"}
        keywords={
          "SPRAVATO esketamine Coral Springs, treatment-resistant depression nasal spray, SPRAVATO REMS center South Florida, esketamine therapy Broward, depression treatment when antidepressants fail, SPRAVATO withMe"
        }
        schemaJson={[
          {
            "@context": "https://schema.org",
            "@type": "MedicalWebPage",
            name: "SPRAVATO® Treatment at Neuro Wellness",
            description:
              "Certified SPRAVATO® REMS treatment center specializing in treatment-resistant depression.",
            url: "https://neurowellnesstms.com/services/spravato",
            provider: {
              "@id": "https://neurowellnesstms.com/#organization",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "MedicalTherapy",
            name: "SPRAVATO® (Esketamine) Treatment",
            description:
              "FDA-approved esketamine nasal spray for treatment-resistant depression and MDD with suicidal thoughts, administered in a certified REMS setting under a REMS monitoring program. Prescription-only; Schedule III controlled substance.",
            url: "/services/spravato",
            provider: {
              "@id": "https://neurowellnesstms.com/#organization",
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqsForSchema.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: {
                "@type": "Answer",
                text: f.a,
              },
            })),
          },
          buildBreadcrumbList(crumbs, "/services/spravato"),
        ]}
      />

      <Navbar />

      <main className="pt-16 sm:pt-20">
        <div className="bg-background py-3">
          <div className="container-px mx-auto max-w-6xl px-4">
            <Breadcrumbs crumbs={crumbs} />
          </div>
        </div>
        {/* ===== HERO ===== */}
        <section className="relative overflow-hidden bg-gradient-navy py-20 text-primary-foreground lg:py-28">
          <div className="pointer-events-none absolute -left-24 top-0 h-72 w-72 rounded-full bg-accent/10 blur-3xl" />
          <div className="pointer-events-none absolute -right-24 bottom-0 h-64 w-64 rounded-full bg-medium-blue/20 blur-3xl" />
          <div className="container-px relative mx-auto max-w-4xl">
            <div className="mb-6 inline-flex items-center gap-2.5 rounded-full border border-white/20 bg-white/10 px-5 py-2 backdrop-blur-md">
              <ShieldCheck className="h-4 w-4 text-accent" />
              <span className="text-sm font-medium text-white/90">
                Certified SPRAVATO® REMS Treatment Center
              </span>
            </div>
            <h1 className="font-headings text-3xl font-bold leading-[1.15] sm:text-4xl md:text-5xl lg:text-5xl xl:text-[3.5rem]">
              When antidepressants haven't worked, there is another option.
            </h1>
            <p className="mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/80 sm:text-xl">
              Neuro Wellness TMS Centers of America is a certified SPRAVATO®
              treatment center in Coral Springs. If you've tried multiple
              antidepressants without the relief you were hoping for, you may be
              a candidate for a different approach — delivered in our office,
              under the care of our clinical team.
            </p>
            <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
              <Button
                asChild
                size="lg"
                className="w-full sm:w-auto sm:min-w-[240px] bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#consult">Request a Consultation</a>
              </Button>
              <p className="text-primary-foreground/80">
                Or call{" "}
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="font-semibold text-white underline decoration-accent/50 underline-offset-4 hover:decoration-accent"
                >
                  {PHONE}
                </a>
                . We'll answer your questions before you commit to anything.
              </p>
            </div>
          </div>
        </section>

        {/* ===== YOU'VE DONE THE WORK ===== */}
        <section className="relative bg-gradient-hero py-16 lg:py-28">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              You've done the work. You're still struggling.
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
              Maybe you've been through two, three, or more antidepressants.
            </h2>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                Maybe some helped a little, or helped for a while. Maybe you've
                started to wonder whether you're just someone medication doesn't
                work for.
              </p>
              <p>
                You're not. Depression that doesn't respond to standard
                antidepressants has a name — treatment-resistant depression —
                and it's more common than most people realize.
              </p>
              <p>
                SPRAVATO® is FDA-approved for adults with treatment-resistant
                depression, and for adults with major depressive disorder who
                are experiencing suicidal thoughts or actions. It works
                differently from oral antidepressants.
              </p>
            </div>

            <h3 className="mt-8 font-headings text-xl font-semibold text-primary">
              You may be a candidate if:
            </h3>
            <ul className="mt-4 space-y-3">
              {[
                "You're 18 or older",
                "You've tried at least two antidepressants at adequate doses without sufficient relief",
                "You're under the care of a prescriber, or open to establishing care with ours",
                "You can arrange a ride home after each treatment",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
                  <span className="text-base text-foreground sm:text-lg">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#consult">Request a Consultation</a>
              </Button>
            </div>
          </div>
        </section>

        {/* ===== NASAL SPRAY ===== */}
        <section className="relative overflow-hidden bg-gradient-navy py-16 text-primary-foreground lg:py-28">
          <div className="pointer-events-none absolute -right-20 top-0 h-64 w-64 rounded-full bg-accent/10 blur-3xl" />
          <div className="container-px relative mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              A nasal spray you take in our office — not another daily pill.
            </span>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-primary-foreground/80 sm:text-lg">
              <p>
                SPRAVATO® (esketamine) is a prescription nasal spray used with
                or without an oral antidepressant to treat adults with
                treatment-resistant depression, and with an oral antidepressant
                to treat depressive symptoms in adults with major depressive
                disorder with suicidal thoughts or actions.
              </p>
              <p>
                Because of its risks, SPRAVATO® is only available through a
                restricted program called the SPRAVATO® REMS. It can only be
                given in healthcare settings certified in that program, and it
                must be self-administered under the supervision of a healthcare
                provider who monitors you afterward.
              </p>
              <p className="font-medium text-primary-foreground">
                Neuro Wellness TMS Centers of America is a certified SPRAVATO®
                REMS treatment center.
              </p>
            </div>
          </div>
        </section>

        {/* ===== VISIT WALKTHROUGH ===== */}
        <section className="relative bg-gradient-hero py-16 lg:py-28">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              Here's exactly what happens when you come in.
            </span>
            <h2 className="mt-3 font-headings text-2xl font-bold text-primary sm:text-3xl lg:text-[2.5rem]">
              Most of the anxiety about SPRAVATO® comes from not knowing what
              the experience is like. So here it is, start to finish.
            </h2>

            <div className="mt-10 space-y-4 sm:mt-14">
              {visitSteps.map((step) => (
                <div
                  key={step.n}
                  className="rounded-2xl border border-border bg-card p-6 shadow-soft transition-all duration-300 hover:border-accent/40 hover:shadow-premium sm:p-7"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-headings text-4xl font-extrabold text-gradient-accent">
                      {step.n}
                    </span>
                    <h3 className="font-headings text-lg font-semibold text-primary">
                      {step.title}
                    </h3>
                  </div>
                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground sm:text-base">
                    {step.body}
                  </p>
                </div>
              ))}
            </div>

            <p className="mt-8 text-sm font-medium text-medium-blue">
              Plan for about two and a half hours from arrival to discharge.
            </p>
          </div>
        </section>

        {/* ===== FIRST FEW MONTHS ===== */}
        <section className="relative overflow-hidden bg-gradient-navy py-16 text-primary-foreground lg:py-28">
          <div className="pointer-events-none absolute -left-20 bottom-0 h-64 w-64 rounded-full bg-medium-blue/20 blur-3xl" />
          <div className="container-px relative mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              What the first few months look like.
            </span>
            <div className="mt-8 space-y-4">
              <div className="rounded-2xl border border-primary-foreground/10 bg-primary-foreground/[0.04] p-6">
                <p className="text-lg">
                  <strong className="font-semibold text-white">
                    Weeks 1–4:
                  </strong>{" "}
                  <span className="text-primary-foreground/80">
                    Twice a week.
                  </span>
                </p>
              </div>
              <div className="rounded-2xl border border-primary-foreground/10 bg-primary-foreground/[0.04] p-6">
                <p className="text-lg">
                  <strong className="font-semibold text-white">
                    Weeks 5–8:
                  </strong>{" "}
                  <span className="text-primary-foreground/80">
                    Once a week.
                  </span>
                </p>
              </div>
              <div className="rounded-2xl border border-primary-foreground/10 bg-primary-foreground/[0.04] p-6">
                <p className="text-lg">
                  <strong className="font-semibold text-white">
                    After that:
                  </strong>{" "}
                  <span className="text-primary-foreground/80">
                    You and your provider decide together whether to continue
                    and how often — weekly or every other week for many
                    patients.
                  </span>
                </p>
              </div>
            </div>
            <p className="mt-6 text-base text-primary-foreground/70 sm:text-lg">
              Your schedule is a clinical decision made with your provider, and
              it can change based on how you respond.
            </p>
          </div>
        </section>

        {/* ===== INSURANCE ===== */}
        <section className="relative bg-gradient-hero py-16 lg:py-28">
          <div className="container-px mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              We'll handle the insurance work.
            </span>
            <div className="mt-6 space-y-4 text-base leading-relaxed text-muted-foreground sm:text-lg">
              <p>
                SPRAVATO® is covered by many commercial plans, as well as
                Medicare and Medicaid, though coverage varies by plan. Before
                you begin, our team verifies your benefits and handles prior
                authorization on your behalf. We'll tell you what your
                out-of-pocket cost will be before your first treatment.
              </p>
              <p>
                The manufacturer also offers a patient support program, SPRAVATO
                withMe, with cost-support options for eligible patients.
                Eligibility rules and restrictions apply.
              </p>
              <p>
                Learn more about SPRAVATO withMe →{" "}
                <a
                  href="https://www.spravato.com/patient-support-program/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-accent underline underline-offset-4 hover:opacity-80"
                >
                  spravato.com/patient-support-program
                </a>
              </p>
            </div>
            <p className="mt-6 text-base text-muted-foreground sm:text-lg">
              Insurance questions? Call{" "}
              <a
                href={`tel:${PHONE_TEL}`}
                className="font-semibold text-primary underline decoration-accent/50 underline-offset-4 hover:decoration-accent"
              >
                {PHONE}
              </a>
              .
            </p>
          </div>
        </section>

        {/* ===== WHY NEURO WELLNESS ===== */}
        <section className="relative overflow-hidden bg-light-blue/40 py-16 lg:py-28">
          <div className="pointer-events-none absolute right-0 top-1/4 h-64 w-64 rounded-full bg-accent/5 blur-3xl" />
          <div className="container-px relative mx-auto max-w-3xl">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              Why Neuro Wellness TMS Centers of America
            </span>
            <ul className="mt-8 space-y-5">
              {[
                "A certified SPRAVATO® REMS treatment center.",
                "Led by Dr. Shalini Katyal, MD, a board-certified psychiatrist licensed in Florida.",
                "Depression is what we specialize in. SPRAVATO® is one of several advanced treatments we offer for depression that hasn't responded to medication — alongside TMS, SAINT TMS, PrTMS, ketamine infusions, psychotherapy, and medication management. If SPRAVATO® isn't the right fit for you, we can talk about what is.",
                "We coordinate with your existing prescriber or therapist so your care stays connected.",
                "Serving Broward, Miami-Dade, and Palm Beach counties from our Coral Springs office.",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 flex-shrink-0 text-accent" />
                  <span className="text-base leading-relaxed text-foreground sm:text-lg">
                    {item}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* ===== NOT THE ONLY OPTION ===== */}
        <section className="relative bg-gradient-hero py-16 lg:py-28">
          <div className="container-px mx-auto max-w-3xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-accent">
              SPRAVATO® isn't the only option.
            </span>
            <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
              If you're not a candidate, or it doesn't feel like the right fit,
              that isn't the end of the conversation. We also treat
              medication-resistant depression with TMS, SAINT TMS, PrTMS,
              ketamine infusions, psychotherapy, and medication management. Your
              consultation is about finding the right treatment, not selling you
              a particular one.
            </p>
            <div className="mt-8">
              <Button
                asChild
                size="lg"
                className="bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
              >
                <a href="#consult">Request a Consultation</a>
              </Button>
            </div>
          </div>
        </section>

        <SpravatoFaqSection />
        <SpravatoConsultSection />

        {/* ===== CONTACT INFO ===== */}
        <section className="relative bg-gradient-hero py-16 lg:py-20">
          <div className="container-px mx-auto max-w-3xl">
            <h2 className="font-headings text-xl font-bold text-primary sm:text-2xl">
              Neuro Wellness TMS Centers of America
            </h2>
            <div className="mt-4 space-y-1 text-base text-muted-foreground sm:text-lg">
              <p>
                9734 West Sample Road, 2nd Floor - Suite B, Coral Springs, FL
                33065
              </p>
              <p>
                <a
                  href={`tel:${PHONE_TEL}`}
                  className="font-semibold text-primary underline decoration-accent/50 underline-offset-4 hover:decoration-accent"
                >
                  {PHONE}
                </a>
              </p>
              <p className="text-medium-blue">
                Monday–Friday 7:00 am–7:00 pm, Saturday 9:00 am–1:00 pm.
              </p>
            </div>
          </div>
        </section>

        <RelatedPages
          heading="Explore SPRAVATO® Scenarios & Other Treatments"
          links={[
            ...allSpravatoScenarioLinks.filter(
              (l) => l.href !== "/services/spravato/",
            ),
            ...allTreatmentsLinks.filter(
              (l) => l.href !== "/services/spravato/",
            ),
            ...allDecisionLinks,
          ]}
        />

        <SpravatoSafetyInfo />
      </main>

      <Footer />
    </div>
  );
};
export default Spravato;
